import React, { useEffect, useState } from 'react'
import { Modal } from '../../../../components/_EOS/Modal'
import { Loading } from '../../../../components/UI'
import { ModalReturnType } from '../../../../hooks/useModal'
import { useApi } from '../../../../hooks/useApi'
import { Status } from '../../../../hooks/useFetchState'
import { Asn } from '../../../../interfaces/Asn/Asn'
import { AsnDetailFullDto } from '../../../../interfaces/Asn/AsnDetailFullDto'
import { getAsnDetailView, downloadAsnFile, downloadAsnResultFile } from '../config'
import { AsnDetailFooterActions } from './SmallComponents'
import { AsnDetailGrid } from './AsnDetailGrid'

interface ASNDetailModalProps {
  viewModalState: ModalReturnType<Asn>
}

export const ASNDetailModal: React.FC<ASNDetailModalProps> = ({
  viewModalState
}) => {
  const asn = viewModalState.extendedProps
  const { state: detailState, callApi: callDetailApi } =
    useApi<AsnDetailFullDto>()

  useEffect(() => {
    if (!asn) return
    callDetailApi(getAsnDetailView(asn.asnId, asn.packingListId)).catch(() => {})
  }, [asn?.asnId, asn?.packingListId])

  return (
    <Modal
      open={viewModalState.isOpen}
      maxWidth='lg'
      title={`Detalle ASN: ${asn?.asnNumber ?? ''}`}
      onClose={viewModalState.closeModal}
      footer={
        asn && (
          <AsnDetailFooter
            processId={detailState.data?.processId ?? null}
            onClose={viewModalState.closeModal}
          />
        )
      }
    >
      {asn && <AsnDetailContent detailState={detailState} />}
    </Modal>
  )
}

const AsnDetailContent: React.FC<{
  detailState: ReturnType<typeof useApi<AsnDetailFullDto>>['state']
}> = ({ detailState }) => {
  const asnData = detailState.data

  const asnBlogsDistinct = Array.from(
    new Set((asnData?.states || []).map((x) => x.asnState))
  ).map((asnState) => {
    const entry = (asnData?.states || []).find((s) => s.asnState === asnState)
    return {
      asnState,
      createdDate: entry?.createdDate,
      createdUser: entry?.createdUser
    }
  })

  if (detailState.status === Status.Loading && !asnData) {
    return <Loading />
  }

  return <AsnDetailGrid asnData={asnData} asnBlogsDistinct={asnBlogsDistinct} />
}

const AsnDetailFooter: React.FC<{
  processId: number | null
  onClose: () => void
}> = ({ processId, onClose }) => {
  const [downloadingAsn, setDownloadingAsn] = useState(false)
  const [downloadingResult, setDownloadingResult] = useState(false)

  const triggerDownload = (response: any) => {
    const raw = window.atob(response.data.file)
    const uInt8Array = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; ++i) {
      uInt8Array[i] = raw.charCodeAt(i)
    }
    const blob = new Blob([uInt8Array], { type: response.data.mimeType })
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', response.data.name)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleDownloadAsn = () => {
    if (!processId) return
    setDownloadingAsn(true)
    downloadAsnFile(processId)
      .then((response) => triggerDownload(response))
      .catch(() => {})
      .finally(() => setDownloadingAsn(false))
  }

  const handleDownloadResult = () => {
    if (!processId) return
    setDownloadingResult(true)
    downloadAsnResultFile(processId)
      .then((response) => triggerDownload(response))
      .catch(() => {})
      .finally(() => setDownloadingResult(false))
  }

  return (
    <AsnDetailFooterActions
      processId={processId}
      downloadingAsn={downloadingAsn}
      downloadingResult={downloadingResult}
      onDownloadAsn={handleDownloadAsn}
      onDownloadResult={handleDownloadResult}
      onClose={onClose}
    />
  )
}
